import { css } from '@emotion/react'
import type { Placement } from '@floating-ui/react'
import { memo, useState } from 'react'

import { type Language, languageFlags, languageLabels, languages } from '@orus.eu/translations'

import { useTranslate } from '../../code-only/localization/index.ts'
import { Button } from '../../components/button/button.tsx'
import { Avatar } from '../../components/index.ts'
import { Row, RowContainer } from '../../components/rows/index.ts'
import { Tooltip } from '../../components/tooltip/tooltip.tsx'
import { borderRadius, colorTokens } from '../../foundations/index.ts'
import { Dropdown } from '../dropdown/dropdown.tsx'

export type LanguageSelectorConfig = {
  currentLanguage: Language
  onLanguageSelected: (language: Language) => void
  availableLanguages?: readonly Language[] | undefined
}

type LanguageSelectorVariant = 'backoffice' | 'funnel'

const variantPresentation: Record<
  LanguageSelectorVariant,
  { placement: Placement; appearance: 'icon' | 'button'; hasTooltip: boolean }
> = {
  backoffice: { placement: 'right-end', appearance: 'icon', hasTooltip: true },
  funnel: { placement: 'bottom-end', appearance: 'button', hasTooltip: false },
}

type NavbarLanguageSelectorProps = {
  config: LanguageSelectorConfig
  /** Rendering context — drives the dropdown placement and the trigger style. */
  variant: LanguageSelectorVariant
}

const getIconButtonStyles = (isActive: boolean) => css`
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 6px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius['20']};
  ${isActive && `background-color: ${colorTokens['color-bg-brand-light']};`}

  &:hover {
    background-color: ${colorTokens['color-bg-base-disable']};
  }

  ${
    isActive &&
    `
      &:hover {
        background-color: ${colorTokens['color-bg-brand-light']};
      }
    `
  }
`

const getButtonStyles = (isActive: boolean) =>
  isActive
    ? css`
        background-color: ${colorTokens['color-bg-brand-light']};

        &:hover {
          background-color: ${colorTokens['color-bg-brand-light']};
        }
      `
    : undefined

const flagStyles = css`
  font-size: 1.5em;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const NavbarLanguageSelector = memo<NavbarLanguageSelectorProps>(function NavbarLanguageSelector({
  config,
  variant,
}) {
  const translate = useTranslate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { placement, appearance, hasTooltip } = variantPresentation[variant]

  const { currentLanguage, onLanguageSelected, availableLanguages } = config

  const selectableLanguages = availableLanguages
    ? languages.filter((language) => availableLanguages.includes(language))
    : languages

  const trigger =
    appearance === 'icon' ? (
      <Button
        variant="text"
        icon="globe-regular"
        ariaLabel={translate('change_language')}
        css={getIconButtonStyles(isDropdownOpen)}
      />
    ) : (
      <Button
        variant="secondary"
        size="medium"
        avatar={<Avatar icon="globe-regular" />}
        ariaLabel={translate('change_language')}
        css={getButtonStyles(isDropdownOpen)}
      />
    )

  const dropdown = (
    <Dropdown trigger={trigger} placement={placement} shouldCloseOnClick={true} onOpenChange={setIsDropdownOpen}>
      <RowContainer variant="border" size="small">
        {selectableLanguages.map((language) => (
          <Row
            key={language}
            title={languageLabels[language]}
            titleVariant="body2"
            leftAvatar={<span css={flagStyles}>{languageFlags[language]}</span>}
            onClick={() => onLanguageSelected(language)}
            selected={language === currentLanguage}
            disabled={language === currentLanguage}
          />
        ))}
      </RowContainer>
    </Dropdown>
  )

  if (hasTooltip) {
    return (
      <Tooltip
        title={isDropdownOpen ? '' : translate('change_language')}
        showArrow={false}
        arrowVerticalPosition="middle"
        arrowHorizontalPosition="left"
      >
        {dropdown}
      </Tooltip>
    )
  }

  return dropdown
})
